import { useEffect, useState } from "react";
import { http, POLL_TOPICS } from "../lib/api";
import PollChart from "../components/PollChart";
import PollAverageCard from "../components/PollAverageCard";

export default function Polls() {
  const [topic, setTopic] = useState("approval");
  const [polls, setPolls] = useState([]);
  const [avg, setAvg] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, a] = await Promise.all([
        http.get("/polls", { params: { topic, limit: 100 } }),
        http.get("/polls/average", { params: { topic, last_n: 5 } }),
      ]);
      setPolls(p.data);
      setAvg(a.data);
    })();
  }, [topic]);

  const meta = POLL_TOPICS.find((t) => t.key === topic);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 animate-fade-in" data-testid="polls-page">
      <p className="section-eyebrow">DASHBOARD</p>
      <h1 className="font-serif-display text-5xl sm:text-6xl leading-none mt-2">
        Encuestas <span className="italic text-rojo">Presidenciales</span>
      </h1>
      <p className="mt-3 text-sub max-w-2xl font-sans">
        Concentrado de las principales casas encuestadoras de México: aprobación presidencial,
        aprobación por partido y aprobación por gobernador. Promedio editorial estilo RCP con las
        últimas cinco mediciones.
      </p>

      <div className="mt-8 flex flex-wrap gap-0 border border-ink w-fit" data-testid="topic-switcher">
        {POLL_TOPICS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTopic(t.key)}
            className={`px-5 py-2 font-mono text-[12px] uppercase tracking-widest transition-colors ${
              topic === t.key ? "bg-ink text-paper" : "bg-paper text-ink hover:bg-ink/10"
            }`}
            data-testid={`topic-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-8">
          <h2 className="font-serif-display text-2xl mb-3">Tendencia · {meta?.label}</h2>
          <PollChart polls={polls} testId="polls-trend-chart" />

          <h2 className="font-serif-display text-2xl mt-10 mb-3">Resultados por casa</h2>
          <div className="border border-ink bg-white overflow-x-auto" data-testid="polls-table">
            <table className="w-full text-left">
              <thead className="bg-ink text-paper">
                <tr>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Fecha</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Casa</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Muestra</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Resultados</th>
                </tr>
              </thead>
              <tbody>
                {polls.map((p) => (
                  <tr key={p.id} className="border-t border-rule" data-testid={`poll-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-sm whitespace-nowrap">{p.date}</td>
                    <td className="px-3 py-2 font-sans text-sm font-semibold whitespace-nowrap">{p.pollster}</td>
                    <td className="px-3 py-2 font-mono text-sm text-sub">{p.sample_size || "—"}</td>
                    <td className="px-3 py-2 text-sm">
                      {p.results.map((r) => (
                        <span key={r.candidate} className="inline-block mr-3">
                          <span className="font-sans">{r.candidate}</span>{" "}
                          <span className="font-mono font-semibold">{r.percentage}%</span>
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
                {!polls.length && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-sub">Sin datos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-5" data-testid="polls-sidebar">
          <PollAverageCard data={avg} title={`Promedio · ${meta?.label}`} />
          <div className="border border-rule bg-white p-4">
            <p className="section-eyebrow">Metodología</p>
            <p className="mt-2 text-sm text-sub leading-relaxed">
              Se integran las mediciones más recientes publicadas por casas encuestadoras
              mexicanas reconocidas. El promedio se calcula como la media simple de los cinco
              estudios más recientes para evitar saltos por casa única. Datos editoriales con fines
              informativos.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
